import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from 'axios';
import { Table, Row, Col, Form, Button } from "react-bootstrap";
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useNavigate } from "react-router-dom";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import "./Home.css"; // Réutilise le design premium du dashboard

const ttLocs = [
  "tunisia mall", "manar city", "jardins d'el menzah",
  "tp-tunisia mallmarge", "tp-manar citymarge", "tp-jardins d'el menzahmarge",
  "tp-tunisiamall", "tp-manarcity", "tp-lesjardinsdelmenzah"
];
const ttLocsNS = ttLocs.map(l => l.replace(/\s+/g, ''));

const isBng = (b) => {
  const name = b.localisation?.name || b.localisation || "";
  const n = name.toString().toLowerCase();
  return n.includes('bagngo') || n.includes('bng');
};

const isEsp = (b) => {
  const name = b.localisation?.name || b.localisation || "";
  const n = name.toString().toLowerCase();
  if (n.includes('espace') || n.includes('e-space')) return true;
  const userName = (b.user?.name || b.userName || "").toString().toLowerCase();
  return userName.includes('espace') || userName.includes('e-space');
};

// Normalisation intelligente des noms d'emplacement en Entités majeures
const classifyEntity = (locName, bill) => {
  const name = (locName || "").toString().toLowerCase();
  const n = name.replace(/\s+/g, '');

  // BagnGo / Bng
  if (n.includes('bagngo') || n.includes('bng')) return 'BagnGo';

  // E-space
  if (n.includes('espace') || n.includes('e-space')) return 'E-space';

  if (bill) {
    const userName = (bill.user?.name || bill.userName || "").toString().toLowerCase();
    const userRole = (bill.user?.role || "").toString().toLowerCase();
    if (userName.includes('espace') || userName.includes('e-space') || userRole === "userespace") {
      return "E-space";
    }
    if (userName.includes('bagngo') || userName.includes('bng') || userRole === "userbagngo") {
      return "BagnGo";
    }
    if (userName.includes('spintoss') || userName.includes('spin') || userRole === "userspintoss") {
      return "Spintoss";
    }
  }

  // Spintoss — détection précise avant Jardins
  if (n.includes('spintoss') || n.includes('spin')) return 'Spintoss';

  // Twin Toys
  if (ttLocsNS.some(l => n.includes(l))) return 'Twin Toys';

  // Twin Park Locations split
  if (n.includes('manar')) return 'Manar City';
  if (n.includes('tunisia')) return 'Tunisia Mall';

  // Jardins d'El Menzah — différencié par activité
  if ((n.includes('jardin') || n.includes('menzah')) && n.includes('twin')) return "Twin Park - Jardins d'El Menzah";
  if ((n.includes('jardin') || n.includes('menzah')) && n.includes('spintoss')) return "Spintoss";
  if (n.includes('jardin') || n.includes('menzah')) return "Twin Park - Jardins d'El Menzah"; // fallback

  if (n.includes('twin')) return 'Manar City'; // fallback

  // Administration
  if (n.includes('administration') || n.includes('admin')) return 'Administration';

  // Wellness / All others
  return 'Wellness';
};

const getChargeEntityName = (locName) => {
  if (!locName) return "Wellness";
  const name = locName.toString().toLowerCase();
  const n = name.replace(/\s+/g, '');

  if (n.includes("manar")) return "Manar City";
  if (n.includes("tunisiamall") || n.includes("tunisiamal")) return "Tunisia Mall";

  // Jardins d'El Menzah — différencié par activité
  if ((n.includes("jardin") || n.includes("menzah")) && n.includes("spintoss")) return "Spintoss";
  if ((n.includes("jardin") || n.includes("menzah")) && n.includes("twin")) return "Twin Park - Jardins d'El Menzah";
  if (n.includes("jardin") || n.includes("menzah")) return "Twin Park - Jardins d'El Menzah";

  if (n.includes("twin")) return "Manar City";
  if (n.includes("art") || n.includes("bagngo") || n.includes("bng")) return "BagnGo";
  if (n.includes("espace") || n.includes("e-space")) return "E-space";
  if (n.includes("spintoss") || n.includes("spin")) return "Spintoss";
  if (n.includes("administration") || n.includes("admin")) return "Administration";
  if (n.includes("jumpzone") || n.includes("jump")) return "Manar City";
  return "Wellness";
};

// Liste ordonnée de toutes les entités à afficher
const entitiesList = [
  "Wellness",
  "BagnGo",
  "E-space",
  "Manar City",
  "Tunisia Mall",
  "Twin Park - Jardins d'El Menzah",
  "Spintoss",
  "Jump Zone",
  "Twin Toys",
  "Administration"
];

function Rbe() {
  const token = localStorage.getItem('token');
  const user = useSelector(state => state.AuthReducer.user);
  const navigate = useNavigate();

  // Sécurité: admin requis
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/Home');
    }
  }, [user, navigate]);

  // Sélection du mois et de l'année
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [billsValises, setBillsValises] = useState([]);
  const [billsPin, setBillsPin] = useState([]);
  const [productsValises, setProductsValises] = useState([]);
  const [productsPin, setProductsPin] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rapports, setRapports] = useState([]);
  const [rapportsTwin, setRapportsTwin] = useState([]);
  const [rapportsSpintoss, setRapportsSpintoss] = useState([]);
  const [chargeExpl, setChargeExpl] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Expanded groups state for collapsible table
  const [expandedGroups, setExpandedGroups] = useState({
    salariales: false,
    exploitation: false,
    autresExploitation: false,
    fiscales: false,
    bancaires: false
  });

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: token ? `Bearer ${token}` : "" };
        const authorisedHeader = { Authorised: token };

        // Fetch broadly to avoid UTC timezone cutoff issues, since frontend filters exactly anyway
        const startDate = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-01`;
        const nextMonthYear = selectedMonth === 11 ? selectedYear + 1 : selectedYear;
        const nextMonthStr = String((selectedMonth + 1) % 12 + 1).padStart(2, '0');
        const endDate = `${nextMonthYear}-${nextMonthStr}-05`;

        const q = `?slim=true&startDate=${startDate}&endDate=${endDate}`;
        const pQ = `?slim=true`;

        const [resValises, resPin, resProdV, resProdPin, resLoc, resRapports, resRapportsTwin, resRapportsSpintoss, resChargeExpl, resCategories] = await Promise.all([
          axios.get(`/api/BillsValises/getAllBillsV${q}`, { headers }),
          axios.get(`/api/Bills/getAllBills${q}`, { headers }),
          axios.get(`/api/ProductV/getProductV${pQ}`, { headers }),
          axios.get(`/api/Product/getProduct${pQ}`, { headers }),
          axios.get("/api/Locations/getAllLocations", { headers: authorisedHeader }),
          axios.get(`/api/Rapports/GetRapports${q}`),
          axios.get(`/api/RapportsTwin/GetRapportsTwin${q}`),
          axios.get(`/api/RapportsSpintoss/GetRapportsSpintoss${q}`),
          axios.get(`/api/ChargeExp/GetChargeExp${q}`),
          axios.get("/api/ChargeExp/GetChargeCategories")
        ]);

        setBillsValises(resValises.data || []);
        setBillsPin(resPin.data || []);
        setProductsValises(resProdV.data || []);
        setProductsPin(resProdPin.data || []);
        const filteredLocs = (resLoc.data || []).filter(loc => {
          const nameLower = (loc.name || "").toLowerCase();
          return !nameLower.includes("family");
        });
        setLocations(filteredLocs);
        setRapports(resRapports.data?.rapports || []);
        setRapportsTwin(resRapportsTwin.data?.RapportsTwin || resRapportsTwin.data?.rapportsTwin || []);
        setRapportsSpintoss(resRapportsSpintoss.data?.RapportsSpintoss || resRapportsSpintoss.data?.rapportsSpintoss || []);
        setChargeExpl(resChargeExpl.data?.ChargeExpl || resChargeExpl.data?.chargeExpl || []);
        setCustomCategories(resCategories.data?.categories || []);
      } catch (err) {
        console.log("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [token, selectedMonth, selectedYear]);

  const months = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1];
  }, []);

  // Calcul du RBE pour le mois sélectionné
  const rbeData = useMemo(() => {
    const cleanRapports = rapports || [];
    const cleanRapportsTwin = rapportsTwin || [];
    const cleanRapportsSpintoss = rapportsSpintoss || [];
    const cleanChargeExpl = chargeExpl || [];

    const categoryToRbeGroup = {
      "Loyers": "autresExploitation",
      "STEG": "autresExploitation",
      "SONEDE": "autresExploitation",
      "Assurance": "autresExploitation",
      "Opérateurs téléphoniques": "autresExploitation",
      "Opérateur Internet": "autresExploitation",
      
      "Achat jouets": "exploitation",
      "Achats Candybar": "exploitation",
      "Carburant": "exploitation",
      "entretien voiture": "exploitation",
      "Fournitures de Bureau": "exploitation",
      
      "Les salaires": "salariales",
      "CNSS": "salariales",
      
      "Déclaration fiscales": "fiscales",
      "Echéancier Fiscal": "fiscales",
      
      "Paiement des Crédits": "bancaires",
      "Agios": "bancaires"
    };

    (customCategories || []).forEach(cat => {
      categoryToRbeGroup[cat.name] = cat.rbeCategory;
    });

    // Cache pour la résolution rapide des localisations
    const locNameCache = new Map();
    locations.forEach(l => {
      if (l && l._id) {
        locNameCache.set(l._id.toString(), l.name?.toLowerCase() || "");
      }
    });

    const resolveLoc = (raw) => {
      if (!raw) return "";
      let idStr = "";
      if (typeof raw === "string") {
        idStr = raw;
      } else if (raw.$oid) {
        idStr = raw.$oid;
      } else if (raw._id) {
        idStr = typeof raw._id === "string" ? raw._id : (raw._id.$oid || raw._id.toString());
      } else if (raw.name) {
        idStr = raw.name;
      } else {
        idStr = raw.toString();
      }
      let name = idStr.toLowerCase();
      if (/^[0-9a-fA-F]{24}$/.test(name)) {
        name = locNameCache.get(name) || "";
      }
      return name;
    };

    const getProductDetailsValises = (item, productsList) => {
      if (!productsList) return { loc: null, name: "Inconnu" };
      const itemIdStr = (item.id || item._id || "").toString();
      const p = productsList.find(x => x._id?.toString() === itemIdStr);
      if (p) {
        return { loc: p.localisation?._id || p.localisation, name: p.name };
      }
      for (const parent of productsList) {
        const sp = parent.subProductsV?.find(x => x._id?.toString() === itemIdStr);
        if (sp) {
          return { loc: sp.localisation?._id || sp.localisation, name: sp.name };
        }
      }
      return { loc: null, name: "Inconnu" };
    };

    const getProductDetailsPin = (item, productsList) => {
      if (!productsList) return { loc: null, name: "Inconnu" };
      const itemIdStr = (item.id || item._id || "").toString();
      const p = productsList.find(x => x._id?.toString() === itemIdStr);
      if (p) {
        return { loc: p.localisation?._id || p.localisation, name: p.name };
      }
      for (const parent of productsList) {
        const sp = parent.subProducts?.find(x => x._id?.toString() === itemIdStr);
        if (sp) {
          return { loc: sp.localisation?._id || sp.localisation, name: sp.name };
        }
      }
      return { loc: null, name: "Inconnu" };
    };

    const dataByEntity = {};
    entitiesList.forEach(ent => {
      const customMap = {};
      (customCategories || []).forEach(cat => {
        customMap[cat.name] = 0;
      });

      dataByEntity[ent] = {
        entity: ent,
        posSales: 0,
        posCaByMethod: { Espece: 0, TPE: 0, TicketResto: 0 },
        reportsCA: 0,
        reportsCaByMethod: { Espece: 0, TPE: 0, TicketResto: 0 },
        ca: 0,
        caByMethod: { Espece: 0, TPE: 0, TicketResto: 0 },
        dailyExpenses: 0,
        operatingCharges: 0,
        netRbe: 0,
        // Catégories détaillées des charges
        details: {
          Loyers: 0, Banques: 0, Fisc: 0, Salaire: 0, Steg: 0, Sonede: 0, CNSS: 0, Assurance: 0,
          Entretien: 0, OperateurTel: 0, OperateurFibre: 0, FournituresBureau: 0, AchatsToys: 0, AchatsCandyBar: 0,
          Carburant: 0, DeclarationFiscales: 0, EcheancierFiscal: 0, PaiementCredits: 0, Agios: 0, Autres: 0,
          customExploitation: 0, customAutresExploitation: 0, customSalariales: 0, customFiscales: 0, customBancaires: 0,
          customAchatsConsommes: 0,
          customCategoriesMap: customMap
        }
      };
    });

    const isInSelectedMonth = (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear;
    };

    const isBillInSelectedMonth = (bill) => {
      const dateStr = bill.date || bill.createdAt;
      if (!dateStr) return false;
      const date = new Date(dateStr);
      return date.getMonth() === selectedMonth && date.getFullYear() === selectedYear && bill.status !== "cancelled";
    };

    // 1. Filtrer et ajouter Wellness, BagnGo, E-space rapports
    cleanRapports.forEach(entry => {
      if (isInSelectedMonth(entry.DateCollect)) {
        let entity = "Wellness";
        if (isBng(entry)) {
          entity = "BagnGo";
        } else if (isEsp(entry)) {
          entity = "E-space";
        }
        if (dataByEntity[entity]) {
          dataByEntity[entity].reportsCA += entry.Amount || 0;
          dataByEntity[entity].dailyExpenses += entry.MonthExpenses || 0;
        }
      }
    });

    // 2. Filtrer et ajouter Twin Park / Twin Toys rapports
    cleanRapportsTwin.forEach(entry => {
      let loc = resolveLoc(entry.localisation);
      if (loc.includes("family")) return; // Éliminer Family Park

      if (isInSelectedMonth(entry.DateCollect)) {
        const twinToysAmount = entry.TwinToys || 0;
        const twinParkAmount = (entry.TotalAmount || 0) - twinToysAmount;

        const total = twinParkAmount + twinToysAmount;
        const tpeProp = total > 0 ? (entry.TotalTpe || 0) / total : 0;
        let subLoc = "Manar City"; // fallback default
        if (loc.includes("tunisia")) {
          subLoc = "Tunisia Mall";
        } else if (loc.includes("jardin") || loc.includes("menzah")) {
          subLoc = "Twin Park - Jardins d'El Menzah";
        } else if (loc.includes("jump") || loc.includes("djerba")) {
          subLoc = "Jump Zone";
        } else if (loc.includes("manar")) {
          subLoc = "Manar City";
        }

        if (dataByEntity[subLoc]) {
          dataByEntity[subLoc].reportsCA += twinParkAmount;
          dataByEntity[subLoc].dailyExpenses += entry.DailyExpenses || 0;
          dataByEntity[subLoc].reportsCaByMethod.TPE += twinParkAmount * tpeProp;
          dataByEntity[subLoc].reportsCaByMethod.Espece += twinParkAmount * (1 - tpeProp);
        }
        if (dataByEntity["Twin Toys"]) {
          dataByEntity["Twin Toys"].reportsCA += twinToysAmount;
          dataByEntity["Twin Toys"].reportsCaByMethod.TPE += twinToysAmount * tpeProp;
          dataByEntity["Twin Toys"].reportsCaByMethod.Espece += twinToysAmount * (1 - tpeProp);
        }
      }
    });

    // 3. Filtrer et ajouter Spintoss rapports
    cleanRapportsSpintoss.forEach(entry => {
      if (isInSelectedMonth(entry.DateCollect)) {
        if (dataByEntity["Spintoss"]) {
          const totalAmount = entry.TotalAmount || 0;
          dataByEntity["Spintoss"].reportsCA += totalAmount;
          dataByEntity["Spintoss"].dailyExpenses += entry.DailyExpenses || 0;

          const tpe = entry.TotalTpe || entry.Tpe || 0;
          const tresto = entry.Tresto || 0;
          const espece = Math.max(0, totalAmount - tpe - tresto);

          dataByEntity["Spintoss"].reportsCaByMethod.TPE += tpe;
          dataByEntity["Spintoss"].reportsCaByMethod.TicketResto += tresto;
          dataByEntity["Spintoss"].reportsCaByMethod.Espece += espece;
        }
      }
    });

    // 4. Filtrer et ajouter POS Valises Bills (au niveau des articles pour correspondre à MargeState)
    billsValises.forEach(bill => {
      if (isBillInSelectedMonth(bill)) {
        bill.items?.forEach(item => {
          if (item.isCancelled) return;
          const details = getProductDetailsValises(item, productsValises);
          let loc = resolveLoc(bill.localisation);
          let name = loc;
          if (!name || /^[0-9a-fA-F]{24}$/.test(name)) {
            const itemLocId = details.loc;
            if (itemLocId) {
              name = locNameCache.get(itemLocId.toString()) || "";
            }
          }
          const entity = classifyEntity(name, bill);
          if (dataByEntity[entity]) {
            const discountFactor = 1 - (bill.discount || 0) / 100;
            const saleAmount = (item.price || 0) * (item.quantity || 1) * discountFactor;
            dataByEntity[entity].posSales += saleAmount;

            const method = (bill.paymentMethod || "Espece").toLowerCase();
            if (method.includes("tpe")) {
              dataByEntity[entity].posCaByMethod.TPE += saleAmount;
            } else if (method.includes("resto") || method.includes("tresto") || method.includes("ticket")) {
              dataByEntity[entity].posCaByMethod.TicketResto += saleAmount;
            } else {
              dataByEntity[entity].posCaByMethod.Espece += saleAmount;
            }
          }
        });
      }
    });

    // 5. Filtrer et ajouter POS Pin Bills (au niveau des articles pour correspondre à MargeState)
    billsPin.forEach(bill => {
      if (isBillInSelectedMonth(bill)) {
        if (bill.isStaffAddition) return;
        bill.items?.forEach(item => {
          if (item.isCancelled) return;
          const details = getProductDetailsPin(item, productsPin);
          let loc = resolveLoc(bill.localisation);
          let name = loc;
          if (!name || /^[0-9a-fA-F]{24}$/.test(name)) {
            const itemLocId = details.loc;
            if (itemLocId) {
              name = locNameCache.get(itemLocId.toString()) || "";
            }
          }
          const entity = classifyEntity(name, bill);
          if (entity === "Wellness") return; // Skip Wellness for Pin bills as in AdvancedAnalytics
          if (dataByEntity[entity]) {
            const discountFactor = 1 - (bill.discount || 0) / 100;
            const saleAmount = (item.price || 0) * (item.quantity || 1) * discountFactor;
            dataByEntity[entity].posSales += saleAmount;

            const method = (bill.paymentMethod || "Espece").toLowerCase();
            if (method.includes("tpe")) {
              dataByEntity[entity].posCaByMethod.TPE += saleAmount;
            } else if (method.includes("resto") || method.includes("tresto") || method.includes("ticket")) {
              dataByEntity[entity].posCaByMethod.TicketResto += saleAmount;
            } else {
              dataByEntity[entity].posCaByMethod.Espece += saleAmount;
            }
          }
        });
      }
    });

    const getEntityIncomeStatement = (ent) => {
      const ca = ent.ca || 0;
      const det = ent.details || {};
      const isSpintoss = ent.entity === "Spintoss";
      const customCategoriesMap = det.customCategoriesMap || {};

      // Les achats consommés (Achats Candybar pour Spintoss + custom achatsConsommes)
      const achatsConsommes = (isSpintoss ? (det.AchatsCandyBar || 0) : 0) + (det.customAchatsConsommes || 0);
      const opsExpenses = ent.dailyExpenses || 0;
      const grossMargin = ca - opsExpenses - achatsConsommes;

      // Exploitation
      const achatsToys = det.AchatsToys || 0;
      const achatsCandyBar = isSpintoss ? 0 : (det.AchatsCandyBar || 0);
      const carburant = det.Carburant || 0;
      const entretien = det.Entretien || 0;
      const fournituresBureau = det.FournituresBureau || 0;
      const customExploitation = det.customExploitation || 0;
      const exploitationTotal = achatsToys + achatsCandyBar + carburant + entretien + fournituresBureau + customExploitation;

      // Autres exploitation
      const loyers = det.Loyers || 0;
      const steg = det.Steg || 0;
      const sonede = det.Sonede || 0;
      const assurance = det.Assurance || 0;
      const operateurTel = det.OperateurTel || 0;
      const operateurFibre = det.OperateurFibre || 0;
      const autresDivers = det.Autres || 0;
      const customAutresExploitation = det.customAutresExploitation || 0;
      const autresExploitationTotal = loyers + steg + sonede + assurance + operateurTel + operateurFibre + autresDivers + customAutresExploitation;

      // Salariales
      const salaire = det.Salaire || 0;
      const cnss = det.CNSS || 0;
      const customSalariales = det.customSalariales || 0;
      const salarialesTotal = salaire + cnss + customSalariales;

      // Fiscales with fallback to Fisc
      let declarationFiscales = det.DeclarationFiscales || 0;
      let echeancierFiscal = det.EcheancierFiscal || 0;
      if (declarationFiscales === 0 && echeancierFiscal === 0 && det.Fisc > 0) {
        declarationFiscales = det.Fisc;
      }
      const customFiscales = det.customFiscales || 0;
      const fiscalesTotal = declarationFiscales + echeancierFiscal + customFiscales;

      // Bancaires with fallback to Banques
      let paiementCredits = det.PaiementCredits || 0;
      let agios = det.Agios || 0;
      if (paiementCredits === 0 && agios === 0 && det.Banques > 0) {
        paiementCredits = det.Banques;
      }
      const customBancaires = det.customBancaires || 0;
      const bancairesTotal = paiementCredits + agios + customBancaires;

      const ebitda = grossMargin - salarialesTotal - exploitationTotal - autresExploitationTotal;
      const netResult = ebitda - fiscalesTotal - bancairesTotal;

      return {
        revenues: ca,
        opsExpenses,
        achatsConsommes,
        grossMargin,
        customCategoriesMap,
        exploitation: {
          achatsToys,
          achatsCandyBar,
          carburant,
          entretien,
          fournituresBureau,
          total: exploitationTotal
        },
        autresExploitation: {
          loyers,
          steg,
          sonede,
          assurance,
          operateurTel,
          operateurFibre,
          autresDivers,
          total: autresExploitationTotal
        },
        salariales: {
          salaire,
          cnss,
          total: salarialesTotal
        },
        fiscales: {
          declarationFiscales,
          echeancierFiscal,
          total: fiscalesTotal
        },
        bancaires: {
          paiementCredits,
          agios,
          total: bancairesTotal
        },
        ebitda,
        netResult
      };
    };

    // Structure for accounting income statement
    const accountingIncomeStatement = {
      revenues: 0,
      opsExpenses: 0,
      achatsConsommes: 0,
      grossMargin: 0,
      customCategoriesMap: {},

      exploitation: {
        achatsToys: 0,
        achatsCandyBar: 0,
        carburant: 0,
        entretien: 0,
        fournituresBureau: 0,
        total: 0
      },
      autresExploitation: {
        loyers: 0,
        steg: 0,
        sonede: 0,
        assurance: 0,
        operateurTel: 0,
        operateurFibre: 0,
        autresDivers: 0,
        total: 0
      },
      salariales: {
        salaire: 0,
        cnss: 0,
        total: 0
      },
      fiscales: {
        declarationFiscales: 0,
        echeancierFiscal: 0,
        total: 0
      },
      bancaires: {
        paiementCredits: 0,
        agios: 0,
        total: 0
      },

      ebitda: 0,
      netResult: 0
    };

    // 6. Filtrer et ajouter les charges d'exploitation (ChargeExp)
    cleanChargeExpl.forEach(charge => {
      if (isInSelectedMonth(charge.DateCollect)) {
        let entity = getChargeEntityName(charge.localisation);
        
        // Affecter les achats Spintoss (Achats Candybar) ou charges étiquetées Spintoss à l'entité Spintoss
        const isSpintossCandybar = charge.objet === "Achats Candybar" || 
          (charge.objet && (charge.objet.toLowerCase().includes("spintoss") || charge.objet.toLowerCase().includes("spin")));
        if (isSpintossCandybar) {
          entity = "Spintoss";
        }

        const amount = charge.TotalAmount || 0;
        const associatedExp = charge.DailyExpenses || 0;

        if (dataByEntity[entity]) {
          dataByEntity[entity].operatingCharges += amount + associatedExp;

          // Dispatcher dans le détail
          const details = dataByEntity[entity].details;
          details.Loyers += charge.Loyers || 0;
          details.Banques += charge.Banques || 0;
          details.Fisc += charge.Fisc || 0;
          details.Salaire += charge.Salaire || 0;
          details.Steg += charge.Steg || 0;
          details.Sonede += charge.Sonede || 0;
          details.CNSS += charge.Cnss || 0;
          details.Assurance += charge.Assurance || 0;
          details.Entretien += charge.Entretien || 0;
          details.OperateurTel += charge.OperateurTel || 0;
          details.OperateurFibre += charge.OperateurFibre || 0;
          details.FournituresBureau += charge.FournituresBureau || 0;
          details.AchatsToys += charge.AchatsToys || 0;
          details.AchatsCandyBar += charge.AchatsCandyBar || 0;
          details.Carburant += charge.Carburant || 0;
          details.DeclarationFiscales += charge.DeclarationFiscales || 0;
          details.EcheancierFiscal += charge.EcheancierFiscal || 0;
          details.PaiementCredits += charge.PaiementCredits || 0;
          details.Agios += charge.Agios || 0;

          // Pour éviter le double comptage, on n'ajoute pas charge.Autres s'il y a déjà une affectation RBE personnalisée
          const hasRbeMapping = categoryToRbeGroup[charge.objet] !== undefined;
          if (!hasRbeMapping) {
            details.Autres += charge.Autres || 0;
          }

          // Map custom charge categories to details custom accumulators
          const rbeGroup = categoryToRbeGroup[charge.objet];
          if (rbeGroup !== undefined) {
            details.customCategoriesMap[charge.objet] = (details.customCategoriesMap[charge.objet] || 0) + (charge.TotalAmount || 0);
          }
          if (rbeGroup === "exploitation") {
            const isStandardExpl = ["Achat jouets", "Achats Candybar", "Carburant", "entretien voiture", "Fournitures de Bureau"].includes(charge.objet);
            if (!isStandardExpl) {
              details.customExploitation += charge.TotalAmount || 0;
            }
          } else if (rbeGroup === "autresExploitation") {
            const isStandardOther = ["Loyers", "STEG", "SONEDE", "Assurance", "Opérateurs téléphoniques", "Opérateur Internet"].includes(charge.objet);
            if (!isStandardOther) {
              details.customAutresExploitation += charge.TotalAmount || 0;
            }
          } else if (rbeGroup === "salariales") {
            const isStandardSalariale = ["Les salaires", "CNSS"].includes(charge.objet);
            if (!isStandardSalariale) {
              details.customSalariales += charge.TotalAmount || 0;
            }
          } else if (rbeGroup === "fiscales") {
            const isStandardFiscale = ["Déclaration fiscales", "Echéancier Fiscal"].includes(charge.objet);
            if (!isStandardFiscale) {
              details.customFiscales += charge.TotalAmount || 0;
            }
          } else if (rbeGroup === "bancaires") {
            const isStandardBancaire = ["Paiement des Crédits", "Agios"].includes(charge.objet);
            if (!isStandardBancaire) {
              details.customBancaires += charge.TotalAmount || 0;
            }
          } else if (rbeGroup === "achatsConsommes") {
            details.customAchatsConsommes += charge.TotalAmount || 0;
          }

          if (associatedExp > 0) {
            details.Autres += associatedExp;
          }
        }
      }
    });

    // Sommes et RBE Nets avec la logique Math.max pour le chiffre d'affaires
    let totalCA = 0;
    let totalDailyExpenses = 0;
    let totalOperatingCharges = 0;

    const formattedList = Object.keys(dataByEntity).map(key => {
      const ent = dataByEntity[key];

      // Chiffre d'Affaires Global = max(CA POS, CA Rapports) pour éviter les doubles comptages
      ent.ca = Math.max(ent.posSales, ent.reportsCA);

      // Est-ce qu'on a des données de paiement dans les rapports pour cette entité ?
      const hasReportPaymentData = ent.reportsCaByMethod.TPE > 0 || ent.reportsCaByMethod.TicketResto > 0;

      if (hasReportPaymentData) {
        // Toujours faire confiance au rapport manuel pour le comptage TPE et Ticket Resto (souvent mal saisi sur le POS)
        ent.caByMethod.TPE = ent.reportsCaByMethod.TPE || 0;
        ent.caByMethod.TicketResto = ent.reportsCaByMethod.TicketResto || 0;
        ent.caByMethod.Espece = Math.max(0, ent.ca - ent.caByMethod.TPE - ent.caByMethod.TicketResto);
      } else {
        // Pour Wellness, BagnGo, E-space : on projette les proportions POS
        if (ent.posSales > 0) {
          const tpeProp = ent.posCaByMethod.TPE / ent.posSales;
          const trestoProp = ent.posCaByMethod.TicketResto / ent.posSales;
          const especeProp = ent.posCaByMethod.Espece / ent.posSales;
          ent.caByMethod.TPE = ent.ca * tpeProp;
          ent.caByMethod.TicketResto = ent.ca * trestoProp;
          ent.caByMethod.Espece = ent.ca * especeProp;
        } else {
          ent.caByMethod.Espece = ent.ca;
          ent.caByMethod.TPE = 0;
          ent.caByMethod.TicketResto = 0;
        }
      }

      ent.ca = parseFloat(ent.ca.toFixed(3));
      ent.caByMethod.Espece = parseFloat(ent.caByMethod.Espece.toFixed(3));
      ent.caByMethod.TPE = parseFloat(ent.caByMethod.TPE.toFixed(3));
      ent.caByMethod.TicketResto = parseFloat(ent.caByMethod.TicketResto.toFixed(3));

      ent.dailyExpenses = parseFloat(ent.dailyExpenses.toFixed(3));
      ent.operatingCharges = parseFloat(ent.operatingCharges.toFixed(3));
      ent.netRbe = parseFloat((ent.ca - ent.dailyExpenses - ent.operatingCharges).toFixed(3));

      totalCA += ent.ca;
      totalDailyExpenses += ent.dailyExpenses;
      totalOperatingCharges += ent.operatingCharges;

      // Calculate entity level income statement
      ent.incomeStatement = getEntityIncomeStatement(ent);

      // Accumulate to global income statement
      const eis = ent.incomeStatement;
      accountingIncomeStatement.revenues += eis.revenues;
      accountingIncomeStatement.opsExpenses += eis.opsExpenses;
      accountingIncomeStatement.achatsConsommes += eis.achatsConsommes;
      accountingIncomeStatement.grossMargin += eis.grossMargin;

      // Accumulate custom categories map
      Object.keys(eis.customCategoriesMap || {}).forEach(catName => {
        accountingIncomeStatement.customCategoriesMap[catName] = 
          (accountingIncomeStatement.customCategoriesMap[catName] || 0) + eis.customCategoriesMap[catName];
      });

      accountingIncomeStatement.exploitation.achatsToys += eis.exploitation.achatsToys;
      accountingIncomeStatement.exploitation.achatsCandyBar += eis.exploitation.achatsCandyBar;
      accountingIncomeStatement.exploitation.carburant += eis.exploitation.carburant;
      accountingIncomeStatement.exploitation.entretien += eis.exploitation.entretien;
      accountingIncomeStatement.exploitation.fournituresBureau += eis.exploitation.fournituresBureau;
      accountingIncomeStatement.exploitation.total += eis.exploitation.total;

      accountingIncomeStatement.autresExploitation.loyers += eis.autresExploitation.loyers;
      accountingIncomeStatement.autresExploitation.steg += eis.autresExploitation.steg;
      accountingIncomeStatement.autresExploitation.sonede += eis.autresExploitation.sonede;
      accountingIncomeStatement.autresExploitation.assurance += eis.autresExploitation.assurance;
      accountingIncomeStatement.autresExploitation.operateurTel += eis.autresExploitation.operateurTel;
      accountingIncomeStatement.autresExploitation.operateurFibre += eis.autresExploitation.operateurFibre;
      accountingIncomeStatement.autresExploitation.autresDivers += eis.autresExploitation.autresDivers;
      accountingIncomeStatement.autresExploitation.total += eis.autresExploitation.total;

      accountingIncomeStatement.salariales.salaire += eis.salariales.salaire;
      accountingIncomeStatement.salariales.cnss += eis.salariales.cnss;
      accountingIncomeStatement.salariales.total += eis.salariales.total;

      accountingIncomeStatement.fiscales.declarationFiscales += eis.fiscales.declarationFiscales;
      accountingIncomeStatement.fiscales.echeancierFiscal += eis.fiscales.echeancierFiscal;
      accountingIncomeStatement.fiscales.total += eis.fiscales.total;

      accountingIncomeStatement.bancaires.paiementCredits += eis.bancaires.paiementCredits;
      accountingIncomeStatement.bancaires.agios += eis.bancaires.agios;
      accountingIncomeStatement.bancaires.total += eis.bancaires.total;

      accountingIncomeStatement.ebitda += eis.ebitda;
      accountingIncomeStatement.netResult += eis.netResult;

      return ent;
    });

    const globalNetRbe = totalCA - totalDailyExpenses - totalOperatingCharges;

    let globalCaEspece = 0;
    let globalCaTPE = 0;
    let globalCaTicketResto = 0;

    formattedList.forEach(ent => {
      globalCaEspece += ent.caByMethod.Espece;
      globalCaTPE += ent.caByMethod.TPE;
      globalCaTicketResto += ent.caByMethod.TicketResto;
    });

    // TPE and Ticket Resto amounts are not subjected to deductions (daily expenses, operating charges are cash only)
    const globalNetRbeTPE = parseFloat(globalCaTPE.toFixed(3));
    const globalNetRbeTicketResto = parseFloat(globalCaTicketResto.toFixed(3));
    const globalNetRbeEspece = parseFloat((globalNetRbe - globalNetRbeTPE - globalNetRbeTicketResto).toFixed(3));

    // Round helper for statements
    const roundValues = (obj) => {
      Object.keys(obj).forEach(k => {
        if (typeof obj[k] === 'number') {
          obj[k] = parseFloat(obj[k].toFixed(3));
        } else if (typeof obj[k] === 'object' && obj[k] !== null) {
          roundValues(obj[k]);
        }
      });
    };
    roundValues(accountingIncomeStatement);
    formattedList.forEach(ent => {
      if (ent.incomeStatement) {
        roundValues(ent.incomeStatement);
      }
    });

    // Calculer le détail global
    const globalChargesDetails = {};

    formattedList.forEach(ent => {
      // 1. Ajouter les champs standards depuis ent.details
      const standards = {
        "Loyers": ent.details.Loyers,
        "Banques": ent.details.Banques,
        "Fisc": ent.details.Fisc,
        "Salaire": ent.details.Salaire,
        "STEG": ent.details.Steg,
        "SONEDE": ent.details.Sonede,
        "CNSS": ent.details.CNSS,
        "Assurance": ent.details.Assurance,
        "Entretien": ent.details.Entretien,
        "Opérateur Tel": ent.details.OperateurTel,
        "Opérateur Fibre": ent.details.OperateurFibre,
        "Fournitures Bureau": ent.details.FournituresBureau,
        "Achats Jouets": ent.details.AchatsToys,
        "Achats Candybar": ent.details.AchatsCandyBar,
        "Carburant": ent.details.Carburant,
        "Déclaration Fiscales": ent.details.DeclarationFiscales,
        "Echéancier Fiscal": ent.details.EcheancierFiscal,
        "Paiement Crédits": ent.details.PaiementCredits,
        "Agios": ent.details.Agios,
        "Autres": ent.details.Autres
      };

      Object.keys(standards).forEach(k => {
        if (standards[k] > 0) {
          globalChargesDetails[k] = (globalChargesDetails[k] || 0) + standards[k];
        }
      });

      // 2. Ajouter les custom categories MAIS exclure celles qui sont déjà couvertes par les champs standards
      const ignoreMap = [
        "Les salaires", "CNSS", "Loyers", "Banques", "STEG", "SONEDE", 
        "Assurance", "Opérateurs téléphoniques", "Opérateur Internet",
        "Achat jouets", "Achats Candybar", "Carburant", "entretien voiture", 
        "Fournitures de Bureau", "Déclaration fiscales", "Echéancier Fiscal",
        "Paiement des Crédits", "Agios", "Fisc"
      ];

      Object.keys(ent.details.customCategoriesMap || {}).forEach(k => {
        if (!ignoreMap.includes(k) && ent.details.customCategoriesMap[k] > 0) {
          globalChargesDetails[k] = (globalChargesDetails[k] || 0) + ent.details.customCategoriesMap[k];
        }
      });
    });

    Object.keys(globalChargesDetails).forEach(k => {
      globalChargesDetails[k] = parseFloat(globalChargesDetails[k].toFixed(3));
    });

    return {
      entities: formattedList,
      totalCA: parseFloat(totalCA.toFixed(3)),
      totalDailyExpenses: parseFloat(totalDailyExpenses.toFixed(3)),
      totalOperatingCharges: parseFloat(totalOperatingCharges.toFixed(3)),
      globalNetRbe: parseFloat(globalNetRbe.toFixed(3)),
      globalCaByMethod: {
        Espece: parseFloat(globalCaEspece.toFixed(3)),
        TPE: parseFloat(globalCaTPE.toFixed(3)),
        TicketResto: parseFloat(globalCaTicketResto.toFixed(3))
      },
      globalNetRbeByMethod: {
        Espece: globalNetRbeEspece,
        TPE: globalNetRbeTPE,
        TicketResto: globalNetRbeTicketResto
      },
      globalChargesDetails,
      accountingIncomeStatement
    };
  }, [rapports, rapportsTwin, rapportsSpintoss, billsValises, billsPin, chargeExpl, selectedMonth, selectedYear, locations, productsValises, productsPin, customCategories]);

  const { entities, totalCA, totalDailyExpenses, totalOperatingCharges, globalNetRbe, globalCaByMethod, globalNetRbeByMethod, globalChargesDetails, accountingIncomeStatement } = rbeData;

  const renderStatementRow = (title, getValue, options = {}) => {
    const {
      isBold = false,
      rowClass = "",
      isSubRow = false,
      isNegative = false,
      isPositive = false,
      onClick = null,
      isCollapsible = false,
      isExpanded = false,
      isNetResult = false
    } = options;

    const prefix = isNegative ? "-" : isPositive ? "+" : "";
    let colorClass = isNegative ? "text-danger" : isPositive ? "text-success" : isBold ? "text-dark" : "text-muted";

    if (rowClass.includes("table-dark")) {
      colorClass = "text-white";
    }

    const textStyle = isSubRow ? { paddingLeft: '32px', fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic' } : {};
    const cursorStyle = onClick ? { cursor: 'pointer' } : {};

    return (
      <tr className={`${rowClass} ${isBold ? 'fw-bold' : ''}`} onClick={onClick} style={cursorStyle}>
        <td style={textStyle}>
          {isCollapsible && (isExpanded ? "▼ " : "▶ ")}
          {title}
        </td>
        {entitiesList.map(entName => {
          const ent = entities.find(e => e.entity === entName);
          const val = ent && ent.incomeStatement ? getValue(ent.incomeStatement) : 0;
          
          let cellColorClass = colorClass;
          let cellStyle = isSubRow ? { color: '#64748b', fontSize: '0.85rem' } : {};
          
          if (isNetResult) {
            if (val > 0) {
              cellColorClass = "text-success fw-bold";
              cellStyle = { ...cellStyle, color: '#05cd99' };
            } else if (val < 0) {
              cellColorClass = "text-danger fw-bold";
              cellStyle = { ...cellStyle, color: '#ee5d50' };
            } else {
              cellColorClass = "text-white";
              cellStyle = { ...cellStyle, color: '#ffffff' };
            }
          }

          return (
            <td key={entName} className={`text-center ${cellColorClass}`} style={cellStyle}>
              {val !== 0 ? `${prefix}${val.toFixed(3)}` : "-"}
            </td>
          );
        })}
        {(() => {
          const totalVal = getValue(accountingIncomeStatement);
          let totalColorClass = colorClass;
          let totalStyle = { 
            backgroundColor: rowClass.includes("table-dark") ? 'inherit' : '#f8fafc', 
            borderLeft: '2px solid #e2e8f0' 
          };

          if (isNetResult) {
            if (totalVal > 0) {
              totalColorClass = "text-success fw-bold";
              totalStyle = { ...totalStyle, color: '#05cd99' };
            } else if (totalVal < 0) {
              totalColorClass = "text-danger fw-bold";
              totalStyle = { ...totalStyle, color: '#ee5d50' };
            } else {
              totalColorClass = "text-white";
              totalStyle = { ...totalStyle, color: '#ffffff' };
            }
          }

          return (
            <td className={`text-center fw-bold ${totalColorClass}`} style={totalStyle}>
              {prefix}{totalVal.toFixed(3)}
            </td>
          );
        })()}
      </tr>
    );
  };

  const pieData = [
    { id: 0, value: globalNetRbe > 0 ? globalNetRbe : 0, label: 'RBE Net', color: '#05cd99' },
    { id: 1, value: totalDailyExpenses, label: 'Ops Quotidien', color: '#ee5d50' },
    { id: 2, value: totalOperatingCharges, label: 'Charges Exp', color: '#8b5cf6' },
  ];

  const chargesByCategoryData = [
    { id: 0, value: accountingIncomeStatement.salariales.total, label: 'Salariales', color: '#3b82f6' },
    { id: 1, value: accountingIncomeStatement.exploitation.total, label: 'Exploitation', color: '#ef4444' },
    { id: 2, value: accountingIncomeStatement.autresExploitation.total, label: 'Autres Exp.', color: '#8b5cf6' },
    { id: 3, value: accountingIncomeStatement.fiscales.total, label: 'Fiscales', color: '#f59e0b' },
    { id: 4, value: accountingIncomeStatement.bancaires.total, label: 'Bancaires', color: '#10b981' }
  ].filter(item => item.value > 0);

  const barChartData = entities.map(e => ({
    entity: e.entity,
    Revenues: e.ca,
    Expenses: parseFloat((e.dailyExpenses + e.operatingCharges).toFixed(3))
  })).filter(item => item.Revenues > 0 || item.Expenses > 0);

  const profitabilityChartData = entities.map(e => ({
    entity: e.entity,
    RbeNet: e.netRbe
  })).filter(item => item.RbeNet !== 0);

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text(`Recapitulatif RBE (Rendement Brut d'Exploitation)`, 14, 15);
    doc.setFontSize(11);
    doc.text(`Periode: ${months[selectedMonth]} ${selectedYear}`, 14, 22);

    doc.autoTable({
      startY: 28,
      head: [['Entité', "Chiffre d'Affaires (CA)", 'Dépenses Ops (Rapports)', 'Charges Exp (Loyers, Salaires...)', 'RBE Net']],
      body: entities.map(e => [
        e.entity,
        e.ca.toFixed(3),
        e.dailyExpenses.toFixed(3),
        e.operatingCharges.toFixed(3),
        e.netRbe.toFixed(3)
      ]),
      foot: [['Total Global', totalCA.toFixed(3), totalDailyExpenses.toFixed(3), totalOperatingCharges.toFixed(3), globalNetRbe.toFixed(3)]]
    });

    const startYCharges = doc.lastAutoTable.finalY + 15;
    doc.text("Ventilation des Charges d'Exploitation Globales", 14, startYCharges);

    const chargesRows = Object.keys(globalChargesDetails)
      .map(k => [k, globalChargesDetails[k].toFixed(3)])
      .filter(row => parseFloat(row[1]) > 0);

    doc.autoTable({
      startY: startYCharges + 5,
      head: [['Nature de la Charge', 'Montant Global (TND)']],
      body: chargesRows,
      foot: [['Total Charges', totalOperatingCharges.toFixed(3)]]
    });

    const startYAccounting = doc.lastAutoTable.finalY + 15;
    doc.text("Etat de Resultat Comptable", 14, startYAccounting);
    doc.autoTable({
      startY: startYAccounting + 5,
      head: [['Poste de Resultat', 'Montant (TND)']],
      body: [
        ["Chiffre d'Affaires (CA) Global", accountingIncomeStatement.revenues.toFixed(3)],
        ["Dépenses Opérationnelles (Rapports)", (-accountingIncomeStatement.opsExpenses).toFixed(3)],
        ["Achats consommés", (-accountingIncomeStatement.achatsConsommes).toFixed(3)],
        ["MARGE OPÉRATIONNELLE BRUTE", accountingIncomeStatement.grossMargin.toFixed(3)],
        ["Charges salariales (Les salaires, CNSS)", (-accountingIncomeStatement.salariales.total).toFixed(3)],
        ["Charges d'exploitation", (-accountingIncomeStatement.exploitation.total).toFixed(3)],
        ["Autres charges d'exploitation", (-accountingIncomeStatement.autresExploitation.total).toFixed(3)],
        ["EXCÉDENT COMPTABLE (RBE / EBITDA)", accountingIncomeStatement.ebitda.toFixed(3)],
        ["Charges Fiscales (Déclaration, Échéancier)", (-accountingIncomeStatement.fiscales.total).toFixed(3)],
        ["Charges Bancaires (Financières)", (-accountingIncomeStatement.bancaires.total).toFixed(3)],
        ["RESULTAT NET COMPTABLE (RBE FINAL)", accountingIncomeStatement.netResult.toFixed(3)]
      ],
      theme: 'grid',
      styles: { halign: 'left' }
    });

    doc.save(`RBE_Recap_${months[selectedMonth]}_${selectedYear}.pdf`);
  };

  const downloadExcel = () => {
    const excelData = entities.map(e => ({
      "Entité / Localisation": e.entity,
      "Chiffre d'Affaires (CA)": e.ca,
      "Dépenses Ops (Rapports)": e.dailyExpenses,
      "Charges d'Exploitation (ChargeExp)": e.operatingCharges,
      "RBE Net": e.netRbe
    }));

    excelData.push({
      "Entité / Localisation": "Total",
      "Chiffre d'Affaires (CA)": totalCA,
      "Dépenses Ops (Rapports)": totalDailyExpenses,
      "Charges d'Exploitation (ChargeExp)": totalOperatingCharges,
      "RBE Net": globalNetRbe
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Recap RBE");

    const chargesDetailExcel = Object.keys(globalChargesDetails).map(k => ({
      "Nature de la Charge": k,
      "Montant (TND)": globalChargesDetails[k]
    }));
    chargesDetailExcel.push({
      "Nature de la Charge": "Total Charges d'Exploitation",
      "Montant (TND)": totalOperatingCharges
    });

    const worksheetCharges = XLSX.utils.json_to_sheet(chargesDetailExcel);
    XLSX.utils.book_append_sheet(workbook, worksheetCharges, "Detail Charges");

    const getRowData = (title, getValue, isNegative = false) => {
      const row = { "Poste de Résultat": title };
      const factor = isNegative ? -1 : 1;
      entitiesList.forEach(entName => {
        const ent = entities.find(e => e.entity === entName);
        const val = ent && ent.incomeStatement ? getValue(ent.incomeStatement) : 0;
        row[entName] = val !== 0 ? val * factor : 0;
      });
      row["Total Global"] = getValue(accountingIncomeStatement) * factor;
      return row;
    };

    const accountingData = [
      getRowData("Chiffre d'Affaires (CA) Global", s => s.revenues),
      getRowData("Dépenses Opérationnelles (Rapports)", s => s.opsExpenses, true),
      getRowData("Achats consommés", s => s.achatsConsommes, true),
      getRowData("MARGE OPÉRATIONNELLE BRUTE", s => s.grossMargin),
      getRowData("Charges salariales (Total)", s => s.salariales.total, true),
      getRowData("  - Les salaires", s => s.salariales.salaire, true),
      getRowData("  - CNSS", s => s.salariales.cnss, true),
      ...(customCategories || []).filter(cat => cat.rbeCategory === 'salariales').map(cat => 
        getRowData(`  - ${cat.name}`, s => (s.customCategoriesMap?.[cat.name] || 0), true)
      ),
      getRowData("Charges d'exploitation (Total)", s => s.exploitation.total, true),
      getRowData("  - Achat jouets", s => s.exploitation.achatsToys, true),
      getRowData("  - Achats Candybar", s => s.exploitation.achatsCandyBar, true),
      getRowData("  - Carburant", s => s.exploitation.carburant, true),
      getRowData("  - entretien voiture", s => s.exploitation.entretien, true),
      getRowData("  - Fournitures de Bureau", s => s.exploitation.fournituresBureau, true),
      ...(customCategories || []).filter(cat => cat.rbeCategory === 'exploitation').map(cat => 
        getRowData(`  - ${cat.name}`, s => (s.customCategoriesMap?.[cat.name] || 0), true)
      ),
      getRowData("Autres charges d'exploitation (Total)", s => s.autresExploitation.total, true),
      getRowData("  - Loyers", s => s.autresExploitation.loyers, true),
      getRowData("  - STEG", s => s.autresExploitation.steg, true),
      getRowData("  - SONEDE", s => s.autresExploitation.sonede, true),
      getRowData("  - Assurance", s => s.autresExploitation.assurance, true),
      getRowData("  - Opérateurs téléphoniques", s => s.autresExploitation.operateurTel, true),
      getRowData("  - Opérateur Internet", s => s.autresExploitation.operateurFibre, true),
      getRowData("  - Dépenses Diverses (ChargeExp)", s => s.autresExploitation.autresDivers, true),
      ...(customCategories || []).filter(cat => cat.rbeCategory === 'autresExploitation').map(cat => 
        getRowData(`  - ${cat.name}`, s => (s.customCategoriesMap?.[cat.name] || 0), true)
      ),
      getRowData("EXCÉDENT COMPTABLE (RBE / EBITDA)", s => s.ebitda),
      getRowData("Charges Fiscales (Total)", s => s.fiscales.total, true),
      getRowData("  - Déclaration fiscales", s => s.fiscales.declarationFiscales, true),
      getRowData("  - Échéancier Fiscal", s => s.fiscales.echeancierFiscal, true),
      ...(customCategories || []).filter(cat => cat.rbeCategory === 'fiscales').map(cat => 
        getRowData(`  - ${cat.name}`, s => (s.customCategoriesMap?.[cat.name] || 0), true)
      ),
      getRowData("Charges Bancaires (Total)", s => s.bancaires.total, true),
      getRowData("  - Paiement des Crédits", s => s.bancaires.paiementCredits, true),
      getRowData("  - Agios", s => s.bancaires.agios, true),
      ...(customCategories || []).filter(cat => cat.rbeCategory === 'bancaires').map(cat => 
        getRowData(`  - ${cat.name}`, s => (s.customCategoriesMap?.[cat.name] || 0), true)
      ),
      getRowData("RÉSULTAT NET COMPTABLE (RBE FINAL)", s => s.netResult)
    ];
    const worksheetAccounting = XLSX.utils.json_to_sheet(accountingData);
    XLSX.utils.book_append_sheet(workbook, worksheetAccounting, "Etat de Resultat");

    XLSX.writeFile(workbook, `RBE_Recap_${months[selectedMonth]}_${selectedYear}.xlsx`);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', background: '#f4f7fe' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3 text-muted fw-bold">Analyse des données en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      <div className="dashboard-container">

        {/* En-tête RBE */}
        <div className="dashboard-header d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <h2>Recap RBE & Ventilation</h2>
            <p className="dashboard-subtitle">Analyse financière : CA vs Charges d'Exploitation par entité</p>
          </div>

          <div className="d-flex align-items-center gap-2 bg-white p-3 rounded-4 shadow-sm border">
            <Form.Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="border-0 shadow-none fw-semibold"
              style={{ width: '150px', cursor: 'pointer' }}
            >
              {months.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </Form.Select>
            <Form.Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border-0 shadow-none fw-semibold"
              style={{ width: '100px', cursor: 'pointer' }}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="kpi-grid">
          <div className="kpi-card">
            <div className="kpi-icon blue">💰</div>
            <div className="kpi-info">
              <h4>CA Global</h4>
              <p>{totalCA.toFixed(3)} TND</p>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon red">📉</div>
            <div className="kpi-info">
              <h4>Dépenses Ops</h4>
              <p>{totalDailyExpenses.toFixed(3)} TND</p>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon purple">💸</div>
            <div className="kpi-info">
              <h4>Charges d'Exploitation</h4>
              <p>{totalOperatingCharges.toFixed(3)} TND</p>
            </div>
          </div>
          <div className="kpi-card">
            <div className="kpi-icon green">📈</div>
            <div className="kpi-info">
              <h4>RBE Net</h4>
              <p className={globalNetRbe >= 0 ? "text-success" : "text-danger"}>
                {globalNetRbe.toFixed(3)} TND
              </p>
            </div>
          </div>
        </div>

        {/* Répartition par Mode de Paiement */}
        <div className="table-card mb-4" style={{ marginTop: '20px' }}>
          <h3>Répartition par Mode de Paiement</h3>
          <div className="table-container mt-3">
            <Table responsive className="dashboard-table">
              <thead>
                <tr>
                  <th>Mode de Paiement</th>
                  <th className="text-end">Chiffre d'Affaires (TND)</th>
                  <th className="text-end">RBE Net (TND)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="fw-bold"><i className="bi bi-cash me-2"></i>Espèce</td>
                  <td className="text-end fw-bold text-primary">{globalCaByMethod.Espece.toFixed(3)}</td>
                  <td className="text-end fw-bold text-success">{globalNetRbeByMethod.Espece.toFixed(3)}</td>
                </tr>
                <tr>
                  <td className="fw-bold"><i className="bi bi-credit-card me-2"></i>TPE</td>
                  <td className="text-end fw-bold text-primary">{globalCaByMethod.TPE.toFixed(3)}</td>
                  <td className="text-end fw-bold text-success">{globalNetRbeByMethod.TPE.toFixed(3)}</td>
                </tr>
                <tr>
                  <td className="fw-bold"><i className="bi bi-ticket-detailed me-2"></i>Ticket Resto</td>
                  <td className="text-end fw-bold text-primary">{globalCaByMethod.TicketResto.toFixed(3)}</td>
                  <td className="text-end fw-bold text-success">{globalNetRbeByMethod.TicketResto.toFixed(3)}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>

        {/* Graphiques */}
        <div className="charts-grid">
          {/* Chart 1: Structure Budgétaire Globale */}
          <div className="chart-card">
            <h3>Structure Budgétaire Globale</h3>
            <div className="d-flex justify-content-center align-items-center">
              <PieChart
                series={[
                  {
                    data: pieData,
                    innerRadius: 60,
                    outerRadius: 100,
                    paddingAngle: 5,
                    cornerRadius: 10,
                  },
                ]}
                height={280}
              />
            </div>
          </div>

          {/* Chart 2: Ventilation des Charges par Nature */}
          <div className="chart-card">
            <h3>Ventilation des Charges par Nature</h3>
            <div className="d-flex justify-content-center align-items-center">
              {chargesByCategoryData.length > 0 ? (
                <PieChart
                  series={[
                    {
                      data: chargesByCategoryData,
                      innerRadius: 60,
                      outerRadius: 100,
                      paddingAngle: 5,
                      cornerRadius: 10,
                    },
                  ]}
                  height={280}
                />
              ) : (
                <p className="text-muted py-5">Aucune charge d'exploitation enregistrée.</p>
              )}
            </div>
          </div>

          {/* Chart 3: Performance par Entité (CA vs Total Dépenses) */}
          <div className="chart-card">
            <h3>Performance par Entité (CA vs Dépenses Totales)</h3>
            <BarChart
              dataset={barChartData}
              xAxis={[{ scaleType: 'band', dataKey: 'entity' }]}
              series={[
                { dataKey: 'Revenues', label: 'CA', color: '#4318FF' },
                { dataKey: 'Expenses', label: 'Dépenses Totales', color: '#ee5d50' }
              ]}
              height={280}
            />
          </div>

          {/* Chart 4: Rentabilité / RBE Net par Entité */}
          <div className="chart-card">
            <h3>Rentabilité / RBE Net par Entité</h3>
            {profitabilityChartData.length > 0 ? (
              <BarChart
                dataset={profitabilityChartData}
                xAxis={[{ scaleType: 'band', dataKey: 'entity' }]}
                series={[
                  { dataKey: 'RbeNet', label: 'RBE Net', color: '#05cd99' }
                ]}
                height={280}
              />
            ) : (
              <p className="text-muted py-5 text-center">Aucune performance à analyser.</p>
            )}
          </div>
        </div>

        {/* Boutons d'exportation */}
        <div className="d-flex gap-3 my-2">
          <Button variant="primary" onClick={downloadPDF} className="px-4 py-2 rounded-3 fw-bold">
            📄 Exporter en PDF
          </Button>
          <Button variant="success" onClick={downloadExcel} className="px-4 py-2 rounded-3 fw-bold">
            📊 Exporter en Excel
          </Button>
        </div>

        {/* Tableau Récapitulatif RBE */}
        <div className="table-card">
          <h3>Calcul du RBE par Entité</h3>
          <div className="table-container mt-4">
            <Table responsive className="dashboard-table">
              <thead>
                <tr>
                  <th>Entité</th>
                  <th>Chiffre d'Affaires (CA)</th>
                  <th>Dépenses Ops (Rapports)</th>
                  <th>Charges Exp (Loyers, Salaires...)</th>
                  <th>RBE Net</th>
                </tr>
              </thead>
              <tbody>
                {entities.map((e, idx) => (
                  <tr key={idx}>
                    <td><strong>{e.entity}</strong></td>
                    <td className="text-dark">{e.ca.toFixed(3)}</td>
                    <td className="text-danger">{e.dailyExpenses.toFixed(3)}</td>
                    <td className="text-purple" style={{ color: 'var(--accent-purple)' }}>{e.operatingCharges.toFixed(3)}</td>
                    <td>
                      <span className={e.netRbe >= 0 ? "text-success" : "text-danger"}>
                        {e.netRbe.toFixed(3)} TND
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="table-total-row">
                  <td>Global</td>
                  <td>{totalCA.toFixed(3)}</td>
                  <td>{totalDailyExpenses.toFixed(3)}</td>
                  <td>{totalOperatingCharges.toFixed(3)}</td>
                  <td>
                    <span className={globalNetRbe >= 0 ? "text-success" : "text-danger"}>
                      {globalNetRbe.toFixed(3)} TND
                    </span>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>

        {/* Ventilation des Charges d'Exploitation */}
        <div className="table-card wellness mb-4">
          <h3>Ventilation des Charges d'Exploitation</h3>
          <p className="text-muted">Répartition par catégorie des dépenses saisies dans ChargeExp</p>
          <Row className="mt-4">
            <Col lg={12} className="mb-4">
              <div className="d-flex flex-nowrap overflow-auto py-3 px-2 gap-3" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}>
                {Object.keys(globalChargesDetails).map((key, i) => {
                  const amount = globalChargesDetails[key];
                  if (amount <= 0) return null;
                  return (
                    <div key={i} className="card shadow-sm border-0 flex-shrink-0" style={{ minWidth: '180px', borderRadius: '12px' }}>
                      <div className="card-body text-center p-3">
                        <h6 className="text-muted mb-2 fw-semibold" style={{ fontSize: '0.85rem' }}>{key}</h6>
                        <h5 className="text-primary fw-bold mb-0">{amount.toFixed(3)} <span style={{ fontSize: '0.8rem' }}>TND</span></h5>
                      </div>
                    </div>
                  );
                })}
                <div className="card shadow-sm border-primary flex-shrink-0" style={{ minWidth: '200px', borderRadius: '12px', backgroundColor: '#eff6ff' }}>
                  <div className="card-body text-center p-3">
                    <h6 className="text-primary mb-2 fw-bold" style={{ fontSize: '0.85rem' }}>TOTAL</h6>
                    <h5 className="text-primary fw-bold mb-0">
                      {Object.values(globalChargesDetails).reduce((a, b) => a + b, 0).toFixed(3)} <span style={{ fontSize: '0.8rem' }}>TND</span>
                    </h5>
                  </div>
                </div>
              </div>
            </Col>

            <Col lg={12}>
              <div className="chart-container d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '300px' }}>
                <h5 className="mb-4">Top Postes de Dépense</h5>
                {totalOperatingCharges > 0 ? (
                  <PieChart
                    series={[
                      {
                        data: Object.keys(globalChargesDetails)
                          .map((key, i) => ({
                            id: i,
                            value: globalChargesDetails[key],
                            label: key
                          }))
                          .filter(item => item.value > 0),
                        innerRadius: 40,
                        outerRadius: 90,
                        paddingAngle: 2,
                        cornerRadius: 5,
                      },
                    ]}
                    height={250}
                  />
                ) : (
                  <p className="text-muted">Aucune charge d'exploitation enregistrée pour ce mois.</p>
                )}
              </div>
            </Col>
          </Row>
        </div>

        {/* État de Résultat Comptable */}
        <div className="table-card mt-5">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h3>État de Résultat Comptable</h3>
              <p className="text-muted mb-0">Synthèse financière structurée par entité et globale pour la période sélectionnée</p>
            </div>
            <div className={`p-3 rounded-4 shadow-sm border d-flex align-items-center gap-3 ${accountingIncomeStatement.netResult >= 0 ? 'bg-success-subtle border-success-subtle' : 'bg-danger-subtle border-danger-subtle'}`} style={{ minWidth: '280px' }}>
              <div className="fs-2">{accountingIncomeStatement.netResult >= 0 ? '🏆' : '⚠️'}</div>
              <div>
                <div className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '1px' }}>RÉSULTAT NET COMPTABLE GLOBAL</div>
                <div className={`fs-4 fw-bold ${accountingIncomeStatement.netResult >= 0 ? 'text-success' : 'text-danger'}`}>
                  {accountingIncomeStatement.netResult.toFixed(3)} TND
                </div>
              </div>
            </div>
          </div>

          <div className="table-container">
            <Table responsive bordered className="dashboard-table align-middle">
              <thead>
                <tr className="table-light">
                  <th style={{ minWidth: '220px' }}>Rubriques Comptables</th>
                  {entitiesList.map(ent => (
                    <th key={ent} className="text-center" style={{ minWidth: '100px' }}>{ent}</th>
                  ))}
                  <th className="text-center" style={{ minWidth: '120px', backgroundColor: '#e2e8f0', borderLeft: '2px solid #cbd5e1' }}>Total Global</th>
                </tr>
              </thead>
              <tbody>
                {/* 1. CA */}
                {renderStatementRow("Chiffre d'Affaires (CA) Global", s => s.revenues, { isBold: true, rowClass: "table-success", isPositive: true })}

                {/* 2. Dépenses Ops */}
                {renderStatementRow("Dépenses Opérationnelles (Rapports)", s => s.opsExpenses, { isNegative: true })}

                {/* Achats consommés */}
                {renderStatementRow("Achats consommés", s => s.achatsConsommes, { isNegative: true })}

                {/* 3. MARGE BRUTE */}
                {renderStatementRow("MARGE OPÉRATIONNELLE BRUTE", s => s.grossMargin, { isBold: true, rowClass: "table-secondary" })}

                {/* 4. Charges Salariales */}
                {renderStatementRow("Charges salariales", s => s.salariales.total, {
                  isBold: true,
                  isNegative: true,
                  onClick: () => toggleGroup('salariales'),
                  isCollapsible: true,
                  isExpanded: expandedGroups.salariales
                })}
                {expandedGroups.salariales && renderStatementRow("Les salaires", s => s.salariales.salaire, { isSubRow: true, isNegative: true })}
                {expandedGroups.salariales && renderStatementRow("CNSS", s => s.salariales.cnss, { isSubRow: true, isNegative: true })}
                {expandedGroups.salariales && (customCategories || []).filter(cat => cat.rbeCategory === 'salariales').map((cat, idx) => 
                  renderStatementRow(cat.name, s => (s.customCategoriesMap?.[cat.name] || 0), { key: `sal-${idx}`, isSubRow: true, isNegative: true })
                )}

                {/* 5. Charges d'Exploitation */}
                {renderStatementRow("Charges d'exploitation", s => s.exploitation.total, {
                  isBold: true,
                  isNegative: true,
                  onClick: () => toggleGroup('exploitation'),
                  isCollapsible: true,
                  isExpanded: expandedGroups.exploitation
                })}
                {expandedGroups.exploitation && renderStatementRow("Achat jouets", s => s.exploitation.achatsToys, { isSubRow: true, isNegative: true })}
                {expandedGroups.exploitation && renderStatementRow("Achats Candybar", s => s.exploitation.achatsCandyBar, { isSubRow: true, isNegative: true })}
                {expandedGroups.exploitation && renderStatementRow("Carburant", s => s.exploitation.carburant, { isSubRow: true, isNegative: true })}
                {expandedGroups.exploitation && renderStatementRow("entretien voiture", s => s.exploitation.entretien, { isSubRow: true, isNegative: true })}
                {expandedGroups.exploitation && renderStatementRow("Fournitures de Bureau", s => s.exploitation.fournituresBureau, { isSubRow: true, isNegative: true })}
                {expandedGroups.exploitation && (customCategories || []).filter(cat => cat.rbeCategory === 'exploitation').map((cat, idx) => 
                  renderStatementRow(cat.name, s => (s.customCategoriesMap?.[cat.name] || 0), { key: `exp-${idx}`, isSubRow: true, isNegative: true })
                )}

                {/* 6. Autres Charges d'Exploitation */}
                {renderStatementRow("Autres charges d'exploitation", s => s.autresExploitation.total, {
                  isBold: true,
                  isNegative: true,
                  onClick: () => toggleGroup('autresExploitation'),
                  isCollapsible: true,
                  isExpanded: expandedGroups.autresExploitation
                })}
                {expandedGroups.autresExploitation && renderStatementRow("Loyers", s => s.autresExploitation.loyers, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("STEG", s => s.autresExploitation.steg, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("SONEDE", s => s.autresExploitation.sonede, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("Assurance", s => s.autresExploitation.assurance, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("Opérateurs téléphoniques", s => s.autresExploitation.operateurTel, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("Opérateur Internet", s => s.autresExploitation.operateurFibre, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && renderStatementRow("Dépenses Diverses (ChargeExp)", s => s.autresExploitation.autresDivers, { isSubRow: true, isNegative: true })}
                {expandedGroups.autresExploitation && (customCategories || []).filter(cat => cat.rbeCategory === 'autresExploitation').map((cat, idx) => 
                  renderStatementRow(cat.name, s => (s.customCategoriesMap?.[cat.name] || 0), { key: `aut-${idx}`, isSubRow: true, isNegative: true })
                )}

                {/* 7. EBITDA / RBE */}
                {renderStatementRow("EXCÉDENT COMPTABLE (RBE / EBITDA)", s => s.ebitda, { isBold: true, rowClass: "table-primary" })}

                {/* 8. Charges Fiscales */}
                {renderStatementRow("Charges Fiscales", s => s.fiscales.total, {
                  isBold: true,
                  isNegative: true,
                  onClick: () => toggleGroup('fiscales'),
                  isCollapsible: true,
                  isExpanded: expandedGroups.fiscales
                })}
                {expandedGroups.fiscales && renderStatementRow("Déclaration fiscales", s => s.fiscales.declarationFiscales, { isSubRow: true, isNegative: true })}
                {expandedGroups.fiscales && renderStatementRow("Échéancier Fiscal", s => s.fiscales.echeancierFiscal, { isSubRow: true, isNegative: true })}
                {expandedGroups.fiscales && (customCategories || []).filter(cat => cat.rbeCategory === 'fiscales').map((cat, idx) => 
                  renderStatementRow(cat.name, s => (s.customCategoriesMap?.[cat.name] || 0), { key: `fisc-${idx}`, isSubRow: true, isNegative: true })
                )}

                {/* 9. Charges Bancaires */}
                {renderStatementRow("Charges Bancaires (Financières)", s => s.bancaires.total, {
                  isBold: true,
                  isNegative: true,
                  onClick: () => toggleGroup('bancaires'),
                  isCollapsible: true,
                  isExpanded: expandedGroups.bancaires
                })}
                {expandedGroups.bancaires && renderStatementRow("Paiement des Crédits", s => s.bancaires.paiementCredits, { isSubRow: true, isNegative: true })}
                {expandedGroups.bancaires && renderStatementRow("Agios", s => s.bancaires.agios, { isSubRow: true, isNegative: true })}
                {expandedGroups.bancaires && (customCategories || []).filter(cat => cat.rbeCategory === 'bancaires').map((cat, idx) => 
                  renderStatementRow(cat.name, s => (s.customCategoriesMap?.[cat.name] || 0), { key: `banc-${idx}`, isSubRow: true, isNegative: true })
                )}

                {/* 10. Résultat Net */}
                {renderStatementRow("RÉSULTAT NET COMPTABLE (RBE FINAL)", s => s.netResult, { isBold: true, rowClass: "table-dark text-white border-top border-3", isNetResult: true })}
              </tbody>
            </Table>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Rbe;
